import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import LoginPrompt from '../../components/LoginPrompt';
import { Layout } from '../../components/Layout';
import HeroTitle from '../../components/HeroTitle';
import Terms from '../../components/Terms';
import { getRunnerCardDataFromHorse } from '../../utils/commonUtils';
import OpenState from '../../components/HomeState/OpenState';
import ResultedState from '../../components/HomeState/ResultedState';
import InRunningState from '../../components/HomeState/InRunningState';
import Loading from '../../components/Loading';
import * as Styled from './styles';
import { sendMetadataEvent } from '../../helpers/googleAnalyticsHelpers/gaHelper';
import GoogleAnalyticsMetaDataObj from '../../helpers/googleAnalyticsHelpers/buildGoogleAnalyticsMetaDataObject';

export const HomePage = ({
    isLoading,
    sweep,
    loggedIn,
    openLogin,
    TermsLink,
    Itv7TermsLink,
    userName,
    customerId,
    ButtonLink,
    hasEventStarted,
    canEnterSweepstake,
    displayCountdownTimer,
    bettingEnabled,
    regStatus,
    promotionsConfig
}) => {
    const googleAnalyticsMetadata = new GoogleAnalyticsMetaDataObj(
        customerId,
        loggedIn,
        regStatus
    );

    useEffect(() => {
        sendMetadataEvent(googleAnalyticsMetadata, 'Sweepstake');
    }, []);

    if (isLoading) {
        return <Loading heading="LOADING" />;
    }

    const { prize, sweepId } = sweep;

    if (!sweepId) {
        // When no sweep data, then render out the basic layout with the error message.
        return <Layout loggedIn={loggedIn} userName={userName} />;
    }

    const startDate = get(sweep, 'event.startDate', null);
    const winningHorses = get(sweep, 'event.winningSelections', []);
    const hasWinners = !!winningHorses.length;
    const winningHorsesData =
        hasWinners &&
        winningHorses.map(winningHorse =>
            getRunnerCardDataFromHorse(winningHorse)
        );

    const title = get(sweep, 'name', '-');
    const eventName = get(sweep, 'event.name', '-');

    let content;

    if (hasWinners) {
        content = (
            <ResultedState
                title={title}
                eventName={eventName}
                prize={prize}
                winningHorsesData={winningHorsesData}
                TermsLink={TermsLink}
                Itv7TermsLink={Itv7TermsLink}
                ButtonLink={ButtonLink}
                bettingEnabled={bettingEnabled}
                promotionsConfig={promotionsConfig}
            />
        );
    } else if (hasEventStarted) {
        content = (
            <InRunningState
                title={title}
                raceName={eventName}
                bettingEnabled={bettingEnabled}
                promotionsConfig={promotionsConfig}
            />
        );
    } else {
        content = (
            <OpenState
                title={title}
                eventName={eventName}
                prize={prize}
                TermsLink={TermsLink}
                loggedIn={loggedIn}
                sweepId={sweepId}
                openLogin={openLogin}
                startDate={startDate}
                canEnterSweepstake={canEnterSweepstake}
                displayCountdownTimer={displayCountdownTimer}
            />
        );
    }

    return (
        <Layout loggedIn={loggedIn} userName={userName}>
            {!loggedIn && hasEventStarted ? (
                <>
                    <Styled.Wrapper>
                        <HeroTitle title={title} prize={prize} />
                        <LoginPrompt openLogin={openLogin} hasEventStarted />
                        <Terms startDate={startDate} TermsLink={TermsLink} />
                    </Styled.Wrapper>
                </>
            ) : (
                content
            )}
        </Layout>
    );
};

HomePage.defaultProps = {
    userName: null,
    customerId: null,
    regStatus: null
};

HomePage.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    customerId: PropTypes.string,
    regStatus: PropTypes.bool,
    sweep: PropTypes.shape({
        prize: PropTypes.string,
        name: PropTypes.string,
        sweepId: PropTypes.string,
        status: PropTypes.string,
        event: PropTypes.shape({
            name: PropTypes.string,
            startDate: PropTypes.string,
            winningSelections: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string,
                    name: PropTypes.string,
                    metaData: PropTypes.shape({
                        clothNumber: PropTypes.string,
                        horseName: PropTypes.string,
                        jockeyName: PropTypes.string,
                        trainerName: PropTypes.string,
                        silk: PropTypes.string
                    })
                })
            )
        })
    }).isRequired,
    loggedIn: PropTypes.bool.isRequired,
    openLogin: PropTypes.func.isRequired,
    TermsLink: PropTypes.func.isRequired,
    Itv7TermsLink: PropTypes.func.isRequired,
    userName: PropTypes.string,
    ButtonLink: PropTypes.func.isRequired,
    hasEventStarted: PropTypes.bool.isRequired,
    canEnterSweepstake: PropTypes.bool.isRequired,
    displayCountdownTimer: PropTypes.bool.isRequired,
    bettingEnabled: PropTypes.bool.isRequired,
    promotionsConfig: PropTypes.shape({
        enabled: PropTypes.bool,
        href: PropTypes.string,
        img: PropTypes.shape({
            mobile: PropTypes.string,
            desktop: PropTypes.string
        })
    }).isRequired
};

export default HomePage;
